import { addDays, addMonths, periodContaining, type PeriodKind } from '@fd/finance-core';
import {
  ChevronLeft,
  ChevronRight,
  CloudOff,
  Eye,
  EyeOff,
  Globe,
  LogOut,
  Moon,
  Plus,
  Search,
  Sun,
  AlertTriangle,
  RefreshCw,
  Check,
} from 'lucide-react';
import type { InvestmentConnection, Transaction } from '@fd/schemas';
import { motion } from 'motion/react';
import { useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createStableContext } from '../app/stable-context.ts';
import { readPreference, writePreference } from '../app/config.ts';
import { useEntities, useHousehold, usePersons } from '../app/data.ts';
import { useI18n } from '../app/i18n.tsx';
import { useInvestmentAutoRefresh } from '../app/investments.ts';
import { hrefFor, type Page, type Route } from '../app/router.tsx';
import { useAuth, useSession } from '../app/session.tsx';
import { useShowTransfers } from '../app/show-transfers.ts';
import { useTheme } from '../app/theme.tsx';
import type { QuickAddKind } from '../pages/QuickAdd.tsx';
import { AutoSettle } from './AutoSettle.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { NotificationBell } from './Notifications.tsx';
import { BrandMark } from '../ui/BrandMark.tsx';
import { DuoIcon } from '../ui/DuoIcon.tsx';
import type { DuoName } from '../ui/duo-icons.ts';
import { Menu } from '../ui/Menu.tsx';
import { Avatar, Segmented } from '../ui/primitives.tsx';

// ------------------------------------------------------------------------------------ period

type PeriodState = { kind: Exclude<PeriodKind, 'CUSTOM'>; anchor: string; setKind(kind: Exclude<PeriodKind, 'CUSTOM'>): void; shift(steps: number): void; reset(): void };
const PeriodContext = createStableContext<PeriodState | null>('period', null);

export function usePeriod(): PeriodState {
  const context = useContext(PeriodContext);
  if (!context) throw new Error('usePeriod outside Shell');
  return context;
}

function PeriodProvider({ children, today }: { children: ReactNode; today: string }) {
  const [kind, setKindState] = useState<Exclude<PeriodKind, 'CUSTOM'>>(() => {
    const stored = readPreference('period');
    return stored === 'WEEK' || stored === 'YEAR' ? stored : 'MONTH';
  });
  const [anchor, setAnchor] = useState(today);
  const value = useMemo<PeriodState>(
    () => ({
      kind,
      anchor,
      setKind(next) {
        writePreference('period', next);
        setKindState(next);
      },
      shift(steps) {
        setAnchor((current) => (kind === 'WEEK' ? addDays(current, 7 * steps) : addMonths(current, kind === 'MONTH' ? steps : 12 * steps)));
      },
      reset: () => setAnchor(today),
    }),
    [kind, anchor, today],
  );
  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

// ------------------------------------------------------------------------------------ sidebar

type NavLabel = 'dashboard' | 'accounts' | 'transactions' | 'recurring' | 'budgets' | 'loans' | 'people' | 'settlements' | 'savings' | 'netWorth' | 'simulation';

/** Each page's two-tone icon, the same in the side panel, the bottom bar and the "More" sheet (as OMGLoft's). */
const PAGE_ICONS: Readonly<Record<NavLabel | 'settings', DuoName>> = {
  dashboard: 'home',
  transactions: 'transactions',
  recurring: 'recurring',
  budgets: 'target',
  accounts: 'wallet',
  loans: 'bank',
  savings: 'savings',
  netWorth: 'growth',
  simulation: 'target',
  people: 'people',
  settlements: 'balance',
  settings: 'settings',
};

/** What the phone's "More" sheet lists: every screen not in the bottom bar. */
const MORE_PAGES: readonly { page: Page; label: 'accounts' | 'recurring' | 'budgets' | 'loans' | 'people' | 'savings' | 'netWorth' | 'simulation' | 'settings' }[] = [
  { page: 'accounts', label: 'accounts' },
  { page: 'recurring', label: 'recurring' },
  { page: 'budgets', label: 'budgets' },
  { page: 'loans', label: 'loans' },
  { page: 'people', label: 'people' },
  { page: 'savings', label: 'savings' },
  { page: 'netWorth', label: 'netWorth' },
  { page: 'simulation', label: 'simulation' },
  { page: 'settings', label: 'settings' },
];

/** The brand as OMGLoft's logo: the mark and the name in two lines, the second in the red. */
function Brand() {
  const { t } = useI18n();
  const [first, ...rest] = t.t('app.name').split(' ');
  return (
    <a className="brand" href={hrefFor({ page: 'dashboard' })} aria-label={t.t('app.name')}>
      <BrandMark />
      <span className="brand__name" aria-hidden="true">
        <b>{first}</b>
        <i>{rest.join(' ')}</i>
      </span>
    </a>
  );
}

/** Light or dark, as OMGLoft's button: the moon and the sun in one place, one turning away as the other comes in. */
function ThemeButton() {
  const { t } = useI18n();
  const { theme, resolved, setTheme } = useTheme();
  return (
    <button
      className="square-btn"
      type="button"
      onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}
      aria-label={t.t('settings.theme')}
      title={`${t.t('settings.theme')}: ${t.t(theme === 'system' ? 'settings.themeSystem' : theme === 'dark' ? 'settings.themeDark' : 'settings.themeLight')}`}
    >
      <span className={`theme-icon${resolved === 'dark' ? ' theme-icon--dark' : ''}`} aria-hidden="true">
        <Moon />
        <Sun />
      </span>
    </button>
  );
}

const LANGUAGES = [
  { value: 'pl', label: 'Polski' },
  { value: 'en', label: 'English' },
] as const;
const LANGUAGE_CODES = [
  { value: 'pl', label: 'PL' },
  { value: 'en', label: 'EN' },
] as const;

/** The language as OMGLoft's menu: a globe and each language in its own name; in the phone's header the codes. */
function LanguageMenu({ short = false, up = false }: { short?: boolean; up?: boolean }) {
  const { language, setLanguage } = useI18n();
  return <Menu options={short ? LANGUAGE_CODES : LANGUAGES} value={language} icon={<Globe aria-hidden="true" />} label="Język / Language" onChange={setLanguage} up={up} />;
}

/** The menu in three groups (spending, wealth, shared), counters where there is something to see. */
function Sidebar({ route }: { route: Route }) {
  const { t } = useI18n();
  const { auth, state } = useAuth();
  const household = useHousehold();
  const email = state.status === 'signedIn' ? state.session.user.email : '';
  const transactions = useEntities<Transaction>('transaction');

  // Uncategorised entries are found through the filter and the banner on Transactions (the owner
  // found a menu item for them redundant). Counted as the list counts them: a pending payment the
  // bank dropped (cancelled) or a reversed one is not on the list, so not in the number either. The
  // period is the one chosen in the banner (the owner: "mam wybrany inny miesiąc, a w panelu z boku
  // pokazuje mi z obecnego miesiąca").
  const period = usePeriod();
  const shown = periodContaining(period.kind, period.anchor);
  const showTransfers = useShowTransfers();
  const inPeriod = useMemo(
    () =>
      (transactions.data ?? []).filter(
        (tx) =>
          tx.deletedAt === null &&
          tx.status !== 'REVERSED' &&
          tx.status !== 'CANCELLED' &&
          tx.kind !== 'BALANCE_ADJUSTMENT' &&
          // The list hides own transfers until asked for, so the count leaves them out too.
          (showTransfers || (tx.kind !== 'TRANSFER' && tx.kind !== 'SETTLEMENT_PAYMENT')) &&
          tx.bookingDate >= shown.start &&
          tx.bookingDate <= shown.end,
      ).length,
    [transactions.data, shown.start, shown.end, showTransfers],
  );
  const count = (value: number) => new Intl.NumberFormat(t.locale).format(value);

  const item = (key: string, href: string, current: boolean, icon: ReactNode, label: string, counter?: { value: number; title: string; alert?: boolean }) => (
    <a key={key} className="nav__item" href={href} aria-current={current ? 'page' : undefined} title={label}>
      {icon}
      <span className="nav__label">{label}</span>
      {counter && counter.value > 0 && (
        <span className={`nav__count${counter.alert ? ' nav__count--alert' : ''}`} title={counter.title}>
          {count(counter.value)}
        </span>
      )}
    </a>
  );
  const page = (label: NavLabel, counter?: { value: number; title: string; alert?: boolean }) =>
    item(
      label,
      hrefFor({ page: label } as Route),
      route.page === label || (label === 'loans' && route.page === 'loan'),
      <DuoIcon name={PAGE_ICONS[label]} />,
      t.t(`nav.${label}`),
      counter,
    );

  return (
    <aside className="sidebar" aria-label={t.t('app.name')}>
      <Brand />
      <nav className="nav">
        {page('dashboard')}
        <div className="nav__section">{t.t('nav.groupSpending')}</div>
        {page('transactions', { value: inPeriod, title: t.t('nav.countInPeriod') })}
        {page('recurring')}
        {page('budgets')}
        <div className="nav__section">{t.t('nav.groupWealth')}</div>
        {page('accounts')}
        {page('loans')}
        {page('savings')}
        {page('netWorth')}
        {page('simulation')}
        <div className="nav__section">{t.t('nav.groupShared')}</div>
        {page('people')}
        {page('settlements')}
        <div className="nav__bottom">{item('settings', hrefFor({ page: 'settings' }), route.page === 'settings', <DuoIcon name="settings" />, t.t('nav.settings'))}</div>
      </nav>
      {/* As OMGLoft's panel: the household with the sign-out square beside it, under it the theme and the language. */}
      <div className="sidebar-footer">
        <div className="sidebar-footer__household">
          <Avatar name={household?.name ?? '?'} color="var(--color-pressed)" />
          <div className="sidebar-footer__text">
            <div className="sidebar-footer__name truncate">{household?.name ?? '—'}</div>
            <div className="sidebar-footer__sub truncate">{email}</div>
          </div>
          <button className="square-btn sidebar-footer__out" type="button" onClick={() => void auth.signOut()} aria-label={t.t('auth.signOut')} title={t.t('auth.signOut')}>
            <LogOut aria-hidden="true" />
          </button>
        </div>
        <div className="sidebar-footer__tools">
          <ThemeButton />
          <LanguageMenu up />
        </div>
      </div>
    </aside>
  );
}

// ------------------------------------------------------------------------------------ top bar

function SyncPill() {
  const { t } = useI18n();
  const { status } = useSession();
  let icon = <Check size={14} className="fg-success" />;
  let text = t.t('sync.synced');
  if (!status.online) {
    icon = <CloudOff size={14} className="fg-warning" />;
    text = status.queued > 0 ? t.plural('sync.pendingOperations', status.queued) : t.t('sync.offline').split('—')[0]!.trim();
  } else if (status.conflicts > 0) {
    icon = <AlertTriangle size={14} className="fg-warning" />;
    text = t.t('sync.conflict');
  } else if (status.failed > 0) {
    icon = <AlertTriangle size={14} className="fg-error" />;
    text = t.plural('sync.failedOperations', status.failed);
  } else if (status.syncing || status.queued > 0) {
    icon = <RefreshCw size={14} className="spin" />;
    text = t.t('sync.syncing');
  }
  // A fixed width: a changing status must not push the search box and buttons around.
  return (
    <a
      className="sync-pill"
      href={hrefFor({ page: 'settings', section: 'sync' })}
      title={[text, status.lastSuccessAt ? t.t('sync.lastSuccess', { time: t.relative(status.lastSuccessAt) }) : null].filter(Boolean).join(' · ')}
    >
      {icon}
      <span className="sync-pill__text">{text}</span>
    </a>
  );
}

/** The overview greets the owner by name and says which period it shows (the owner's brief). */
function Greeting() {
  const { t } = useI18n();
  const household = useHousehold();
  const persons = usePersons();
  const period = usePeriod();
  const current = periodContaining(period.kind, period.anchor);
  const name = persons.find((p) => p.id === household?.ownerPersonId)?.displayName ?? null;
  const hour = new Date().getHours();
  const hello = t.t(hour < 12 && hour >= 5 ? 'overview.greeting.morning' : hour < 18 && hour >= 5 ? 'overview.greeting.afternoon' : 'overview.greeting.evening');
  const label = period.kind === 'MONTH' ? t.date(current.start, 'monthYear') : period.kind === 'YEAR' ? current.start.slice(0, 4) : `${t.date(current.start, 'dayMonth')} – ${t.date(current.end, 'dayMonth')}`;
  return (
    <div className="page-heading__copy">
      <h1 className="greeting">{name ? `${hello}, ${name}` : hello}</h1>
      <p className="greeting__sub">{t.t('overview.greetingSub', { period: label })}</p>
    </div>
  );
}

/**
 * Every other page's heading, built as the greeting is (so of its height): the chosen period in large
 * type, and a line on what the page shows; without a period only that line, the large one held unseen.
 */
function PageHeadingCopy({ route, showPeriod }: { route: Route; showPeriod: boolean }) {
  const { t } = useI18n();
  const period = usePeriod();
  const current = periodContaining(period.kind, period.anchor);
  const label = period.kind === 'MONTH' ? t.date(current.start, 'monthYear') : period.kind === 'YEAR' ? current.start.slice(0, 4) : `${t.date(current.start, 'dayMonth')} – ${t.date(current.end, 'dayMonth')}`;
  const page = route.page === 'transactions' && route.view === 'upcoming' ? 'upcoming' : route.page === 'transactions' && route.view === 'reports' ? 'reports' : route.page;
  return (
    <div className="page-heading__copy">
      <h1 className="greeting page-heading__period" style={showPeriod ? undefined : { visibility: 'hidden' }} aria-hidden={showPeriod ? undefined : true}>
        {label}
      </h1>
      <p className="greeting__sub">{t.dynamic(`heading.${page}`)}</p>
    </div>
  );
}

function PeriodSwitcher() {
  const { t } = useI18n();
  const period = usePeriod();
  const current = periodContaining(period.kind, period.anchor);
  const label =
    period.kind === 'MONTH'
      ? t.date(current.start, 'monthYear')
      : period.kind === 'YEAR'
        ? current.start.slice(0, 4)
        : `${t.date(current.start, 'dayMonth')} – ${t.date(current.end, 'dayMonth')}`;
  return (
    <div className="topbar__period">
      <Segmented
        label={t.t('period.month')}
        value={period.kind}
        onChange={period.setKind}
        options={[
          { value: 'WEEK', label: t.t('period.week') },
          { value: 'MONTH', label: t.t('period.month') },
          { value: 'YEAR', label: t.t('period.year') },
        ]}
      />
      <button className="icon-btn" type="button" onClick={() => period.shift(-1)} aria-label={t.t('period.previous')}>
        <ChevronLeft />
      </button>
      <button className="period-label" type="button" onClick={period.reset} style={{ background: 'none', border: 0, cursor: 'pointer' }}>
        {/* On a phone the month's short name: "Październik 2026" ran into the arrow beside it. */}
        <span className="period-label__long">{label}</span>
        <span className="period-label__short" aria-hidden="true">
          {period.kind === 'MONTH' ? `${t.date(current.start, 'month')} ${current.start.slice(0, 4)}` : label}
        </span>
      </button>
      <button className="icon-btn" type="button" onClick={() => period.shift(1)} aria-label={t.t('period.next')}>
        <ChevronRight />
      </button>
    </div>
  );
}

export function Shell({
  route,
  title,
  showPeriod,
  onQuickAdd,
  today,
  children,
}: {
  route: Route;
  title: string;
  /** The page follows the chosen period; without it the switch stays in place, invisible. */
  showPeriod: boolean;
  onQuickAdd(kind?: QuickAddKind): void;
  today: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { privacy, setPrivacy } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [route.page]);
  // Trading 212 figures stay fresh while the app is open, not only when the server's round runs.
  const { householdId, requestSync } = useSession();
  const investments = useEntities<InvestmentConnection>('investmentConnection');
  useInvestmentAutoRefresh(householdId, investments.data ?? [], requestSync);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      const target = event.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      if (!typing && event.key.toLowerCase() === 'n' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        onQuickAdd();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onQuickAdd]);

  return (
    <PeriodProvider today={today}>
      {/* A month paid back by bank transfer settles itself, on whichever device is open. */}
      <AutoSettle />
      <div className="shell">
        <a className="skip-link" href="#main-content" onClick={(event) => {
          event.preventDefault();
          document.getElementById('main-content')?.focus();
        }}>{t.t('dashboard.skipToContent')}</a>
        <Sidebar route={route} />
        <div className="main">
          {/* The phone's header as OMGLoft's: the logo, the language and the theme (the side panel is hidden). */}
          <header className="phone-head">
            <Brand />
            <span className="phone-head__spacer" />
            <LanguageMenu short />
            <ThemeButton />
          </header>
          {/* The page's head: the bar with the title and the tools, under it the greeting or the label with the
              period switcher (styles/omgloft.css). */}
          <div className="masthead">
            <header className="topbar">
              <h1 className="topbar__title">{title}</h1>
              <div className="topbar__spacer" />
              <button className="search-trigger" type="button" aria-label={t.t('common.search')} aria-keyshortcuts="Control+K" onClick={() => setPaletteOpen(true)}>
                <Search aria-hidden="true" />
                <span>{t.t('common.search')}</span>
                <kbd>Ctrl K</kbd>
              </button>
              <button className="icon-btn" type="button" aria-pressed={privacy} onClick={() => setPrivacy(!privacy)} aria-label={t.t('settings.privacyMode')} title={t.t('settings.privacyMode')}>
                {privacy ? <EyeOff /> : <Eye />}
              </button>
              <NotificationBell />
              <SyncPill />
              <button className="btn btn--primary" type="button" onClick={() => onQuickAdd()}>
                <Plus aria-hidden="true" />
                {t.t('common.add')}
              </button>
            </header>
            {/* The same second row on every page, so the banner keeps the dashboard's height (the owner,
                01.10.2026: "baner ma inną wysokość — zrób stałą z pulpitu wszędzie"). */}
            <div className="page-heading">
              {route.page === 'dashboard' ? <Greeting /> : <PageHeadingCopy route={route} showPeriod={showPeriod} />}
              {showPeriod ? (
                <PeriodSwitcher />
              ) : (
                // A page without a period keeps the switch, invisible: the banner does not change
                // height from page to page (the owner, 29.09.2026 and 01.10.2026).
                <div className="page-heading__held" aria-hidden="true" inert>
                  <PeriodSwitcher />
                </div>
              )}
            </div>
          </div>
          {/* A new page settles in (fade and a few pixels of rise); the old one simply goes. */}
          <motion.main id="main-content" tabIndex={-1} key={route.page} className="content" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}>
            {children}
          </motion.main>
        </div>
        <nav className="bottom-nav" aria-label={t.t('app.name')}>
          <a href={hrefFor({ page: 'dashboard' })} aria-current={route.page === 'dashboard' ? 'page' : undefined}>
            <DuoIcon name={PAGE_ICONS.dashboard} size={26} />
            {t.t('nav.home')}
          </a>
          <a href={hrefFor({ page: 'transactions' })} aria-current={route.page === 'transactions' ? 'page' : undefined}>
            <DuoIcon name={PAGE_ICONS.transactions} size={26} />
            {t.t('nav.transactions')}
          </a>
          <button type="button" className="bottom-nav__add" onClick={() => onQuickAdd()} aria-label={t.t('common.add')}>
            <Plus aria-hidden="true" />
          </button>
          <a href={hrefFor({ page: 'settlements' })} aria-current={route.page === 'settlements' ? 'page' : undefined}>
            <DuoIcon name={PAGE_ICONS.settlements} size={26} />
            {t.t('nav.settlements')}
          </a>
          <button type="button" aria-expanded={moreOpen} aria-current={MORE_PAGES.some((m) => m.page === route.page) ? 'page' : undefined} onClick={() => setMoreOpen(!moreOpen)}>
            <DuoIcon name="more" size={26} />
            {t.t('common.more')}
          </button>
        </nav>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onQuickAdd={onQuickAdd} />
        {moreOpen && (
          <div className="more-sheet" onClick={() => setMoreOpen(false)}>
            <nav className="more-sheet__panel" aria-label={t.t('common.more')} onClick={(e) => e.stopPropagation()}>
              {MORE_PAGES.map((item) => (
                <a
                  key={item.page}
                  className="more-sheet__item"
                  href={hrefFor({ page: item.page } as Route)}
                  aria-current={route.page === item.page ? 'page' : undefined}
                  onClick={() => setMoreOpen(false)}
                >
                  <DuoIcon name={PAGE_ICONS[item.label]} size={26} />
                  {t.t(`nav.${item.label}`)}
                </a>
              ))}
            </nav>
          </div>
        )}
      </div>
    </PeriodProvider>
  );
}
